/* eslint-disable no-param-reassign */
/* eslint-disable no-bitwise */
/* eslint-disable consistent-return */
import moment from 'moment';
import { regex } from '../../constants';

export const isEmpty = value => !value || value.trim?.() === '';

export const EMAIL_MAX_LENGTH = 50;

const join = (rules) => (value, data) => rules.map(rule => rule(value, data)).filter(error => !!error)[0];

export function isValidEmail(value) {
    return regex.email.test(value.trim());
}

export function optionalEmail(value) {
    if (isEmpty(value)) {
        return undefined;
    }
    if (value.length > EMAIL_MAX_LENGTH) {
        return `Email must be no more than ${EMAIL_MAX_LENGTH} characters.`;
    }
    if (!isValidEmail(value)) {
        return 'This does not appear to be a valid email';
    }
}

export function requiredEmail(value) {
    if (isEmpty(value)) {
        return 'This field is required.';
    }

    return optionalEmail(value);
}

export function optionalPhoneNumber(value) {
    if (!isEmpty(value) && !regex.phoneNumber.test(value)) {
        return 'Please provide a valid phone number. Country codes (\'+1\') are not required.';
    }
}

export function optionalZipCode(value) {
    if (!isEmpty(value) && !regex.zipCode.test(value)) {
        return 'Please provide a valid zip code.';
    }
}

export function requiredZipCode(value) {
    if (isEmpty(value)) {
        return 'This field is required';
    }
    return optionalZipCode(value);
}

export function requiredPhoneNumber(value) {
    if (isEmpty(value)) {
        return 'This field is required';
    }
    return optionalPhoneNumber(value);
}

export function checkBoxSelected(value) {
    if (isEmpty(value) || value === false) {
        return 'This field is required';
    }
}

export function requiredBoolean(value) {
    if (value !== true && value !== false) {
        return 'This field is required';
    }
}

export function required(value) {
    if (isEmpty(value)) {
        return 'This field is required';
    }
}

export function minLength(min) {
    return value => {
        if (!isEmpty(value) && value.length < min) {
            return `Must be at least ${min} characters`;
        }
    };
}

export function maxLength(max) {
    return value => {
        if (!isEmpty(value) && value.length > max) {
            return `Must be no more than ${max} characters`;
        }
    };
}

export function maxWordLength(max) {
    return value => {
        if (!isEmpty(value) && value.split(' ').length > max) {
            return `Must be no more than ${max} words`;
        }
    };
}

export function integer(value) {
    if (!isEmpty(value) && !Number.isInteger(Number(value))) {
        return 'Must be an integer';
    }
}

export function positiveInteger(value) {
    if (!isEmpty(value) && (!Number.isInteger(Number(value)) || Number(value) < 0)) {
        return 'Must be a positive integer';
    }
}

export function numeric(value) {
    if (!isEmpty(value) && Number.isNaN(Number(value))) {
        return 'Value must be numeric';
    }
}

export function range(min, max, except) {
    return value => {
        if (except?.includes(parseInt(value))) {
            return `Value can't be ${value}`;
        }
        if (!isEmpty(value) && (value < min)) {
            return `Value can't be less than ${min}`;
        }
        if (!isEmpty(value) && (value > max)) {
            return `Value can't be greater than ${max}`;
        }
    };
}

export function oneOf(enumeration) {
    return value => {
        if (!~enumeration.indexOf(value)) {
            return `Must be one of: ${enumeration.join(', ')}`;
        }
    };
}

export function match(field) {
    return (value, data) => {
        if (data) {
            if (value !== data[field]) {
            return 'Do not match';
            }
        }
    };
}

export function checkBirthDate(month, year, day) {
    // Check for empty values
    if (month === null || month === undefined || month === '' ||
        year === null || year === undefined || year === '' ||
        day === null || day === undefined || day === '') {
        return 'This field is required.';
    }

    // Validate that month is a string
    if (typeof month !== 'string') {
        return 'Please enter a valid month.';
    }

    // Parse month string to month number (0-11) using moment
    // month is expected to be a string like "January", "March", etc.
    const monthMoment = moment(month, 'MMMM', true); // 'MMMM' = full month name, strict parsing
    if (!monthMoment.isValid()) {
        return 'Please enter a valid month name.';
    }
    const monthNum = monthMoment.month(); // Returns 0-11

    // Convert year and day to numbers for validation
    const yearNum = Number(year);
    const dayNum = Number(day);

    // Validate that year and day are valid numbers
    if (Number.isNaN(yearNum) || Number.isNaN(dayNum)) {
        return 'Please enter valid numbers for year and day.';
    }

    // Validate year range (reasonable age limits: 0-150 years old)
    // Note: We allow future years here and let the date comparison handle them
    // to provide a more specific error message
    const currentYear = moment().year();
    const minYear = currentYear - 150;
    if (yearNum < minYear) {
        return 'Please enter a valid year.';
    }

    // Validate day range (basic check before creating date)
    if (dayNum < 1 || dayNum > 31) {
        return 'Please enter a valid day (1-31).';
    }

    // Validate that year and day are integers
    if (!Number.isInteger(yearNum) || !Number.isInteger(dayNum)) {
        return 'Please enter whole numbers for year and day.';
    }

    // Create moment date object and validate it's a valid date
    const birthDate = moment().year(yearNum).month(monthNum).date(dayNum);

    // Check if the date is valid (handles cases like completely invalid dates)
    if (!birthDate.isValid()) {
        return 'Please enter a valid date.';
    }

    // Verify that the created date matches the input values
    // This catches cases where moment adjusted invalid dates (e.g., Feb 30 -> March 2, April 31 -> May 1)
    if (birthDate.year() !== yearNum || birthDate.month() !== monthNum || birthDate.date() !== dayNum) {
        return 'Please enter a valid date.';
    }

    // Compare with today using moment (consistent types)
    const today = moment().startOf('day');
    const birthDateStart = birthDate.startOf('day');

    // Birthdate cannot be today or in the future
    if (birthDateStart.isSameOrAfter(today)) {
        return 'Birthdate should not be greater than today';
    }

    // Return undefined for valid dates (no error)
    return undefined;
}

export function password(value) {
    if (!isEmpty(value) && !regex.password.test(value)) {
        return 'Password does not meet complexity requirements';
    }
}

export function requiredPassword(value) {
    let error = password(value);
    if (isEmpty(value)) {
        error = 'This field is required.';
    }
    return error;
}

export function createValidator(rules) {
    return (data = {}) => {
        const errors = {};
        Object.keys(rules).forEach((key) => {
            const rule = rules[key];
            const validators = [].concat(rule).filter(v => typeof v === 'function');
            const error = join(validators)(data[key], data);
            if (error) {
                errors[key] = error;
            }
        });
        return errors;
    };
}

export function createReactFormValidator(fn) {
    return (value) => {
        if (!Array.isArray(fn)) {
            fn = [fn];
        }
        let error;
        fn.forEach(validator => {
            const resp = validator(value);
            if (!isEmpty(resp)) {
                error = resp;
                return false;
            }
        });
        return { error };
    };
}

export function url(value) {
    if (!isEmpty(value) && !regex.url.test(value.trim())) {
        return 'This does not appear to be a valid URL.';
    }
}

export function requiredUrl(value) {
    if (isEmpty(value)) {
        return 'This field is required.';
    }

    const error = url(value);
    if (error) {
        return error;
    }

    if (!/^https?:\/\//i.test(value.trim())) {
        return 'Streaming link must be a valid http or https URL.';
    }
}

export const validateCommaSeparatedEmails = (value, limit) => {
    if (!value) {
        return 'Please enter at least one email address.';
    }
    // Split and trim email addresses
    const emails = value.split(',').map(email => email.trim()).filter(email => !isEmpty(email));
    if (emails.length === 0) {
        return 'Please enter at least one email address.';
    }

    if (emails.length > limit) {
        return `Please enter a maximum of ${limit} email addresses.`;
    }

    // Validate individual email addresses
    const invalidEmails = emails.filter(email => !regex.email.test(email));
    if (invalidEmails.length > 0) {
        const plural = invalidEmails.length > 1 ? 'es' : '';
        const missingComma = undefined !== emails.find(email => /\s/g.test(email)) ? ' (missing a comma?)' : '';
        const invalidEmailList = invalidEmails.join(', ');
        return `Invalid email address${plural}${missingComma}: ${invalidEmailList}`;
    }
    return null; // Return null if all email addresses are valid
};

export const validateEventAllottedMatchTimeScoringFormat = (value, eventAllottedMatchTimesValue) => {
    if (isEmpty(value)) {
        return 'This field is required';
    }

    const list = eventAllottedMatchTimesValue.filter(matchTime => (matchTime.scoringTypeId === value));
    if (list.length > 1) {
        return 'This Scoring format has already been set with allotted time';
    }
};
